import { useState, useEffect, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from 'axios'
import { API_CONFIG } from '../../config/apiConfig'
import { showToast } from '../../utils/messageUtil'
import { useUser } from '../../contexts/UserContext'
import { handleJSAPIAccess } from '../../utils/auth_access_util'
import './index.css'
export default function Reserve() {
    const navigate = useNavigate()
    const location = useLocation()
    const { userInfo, loading: userLoading, refreshUserInfo } = useUser()

    const [hasRefreshed, setHasRefreshed] = useState(false)

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.user_id || userInfo.open_id
        // 预约页面只需要user_id
        return hasUserId !== ''
    }

    // 如果用户信息缺失，重新获取
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!userLoading && !isUserInfoComplete() && !hasRefreshed) {
            console.log('预约页面：用户信息不完整，重新获取...')
            setHasRefreshed(true)
            refreshUserInfo()
        }
    }, [userLoading, userInfo, refreshUserInfo, hasRefreshed])
    // 从localStorage恢复表单数据，如果没有则使用默认值
    const getInitialFormData = () => {
        const savedData = localStorage.getItem('reserveFormData');
        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                // 清空同行人和用车人字段，避免自动带出上次选择
                parsedData.vehicleUserName = '';
                parsedData.vehicleUserCode = '';
                parsedData.passengers = '';
                parsedData.passengersCode = '';
                return parsedData;
            } catch (error) {
                console.error('解析表单数据失败:', error);
            }
        }
        return {
            applicantName: '',
            applicantPhone: '',
            applyTime: new Date().toISOString().split('T')[0],
            applicantDept: '',
            employeeId: '',
            applicantOpenId: '',
            plateNumber: '',
            driveType: '1',
            // 初始时间设置为当前时间
            expectedStartTime: new Date().toISOString(),
            expectedEndTime: new Date().toISOString(),
            expectedLocation: '',
            destination: '',
            vehicleUserName: '', // 用车人姓名(逗号分隔)
            vehicleUserCode: '', // 用车人openId(逗号分隔)
            passengers: '',
            passengersCode: '',
            reason: ''
        };
    };
    
    const [formData, setFormData] = useState(getInitialFormData());
    // 目的地数组管理
    const [destinations, setDestinations] = useState([]);
    // 添加目的地输入框状态
    const [showAddDestination, setShowAddDestination] = useState(false);
    const [newDestinationInput, setNewDestinationInput] = useState('');

    const [submitting, setSubmitting] = useState(false)
    const [vehicleUsageTimes, setVehicleUsageTimes] = useState([])
    const [isPersonal, setIsPersonal] = useState(0) // 0=本人申请, 1=代人申请

    // 获取车辆使用时间数据
    const fetchVehicleUsageTimes = useCallback(async (plateNumber) => {
        try {
            if (!plateNumber) {
                setVehicleUsageTimes([])
                return
            }
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/getVehicleAvailableTime`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                plateNumber: plateNumber
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response.data && response.data.code === 200) {
                const usageTimes = response.data.data || []
                // 获取当前申请时间（使用最新的表单数据）
                const applyTime = new Date(formData.applyTime)
                // 过滤出开始时间大于等于申请时间的记录
                const filteredUsageTimes = usageTimes.filter(usage => {
                    const startTime = new Date(usage.startTime)
                    return startTime >= applyTime
                })
                setVehicleUsageTimes(filteredUsageTimes)
            } else {
                setVehicleUsageTimes([])
            }
        } catch (error) {
            console.error('获取车辆使用时间失败:', error)
            setVehicleUsageTimes([])
        }
    }, [formData.applyTime])

    // 当用户信息加载完成后，更新表单数据
    useEffect(() => {
        if (userInfo) {
            // console.log('用户信息已加载:', userInfo);
            // console.log('部门信息:', userInfo.department_name);
            // console.log('部门路径:', userInfo.department_path);

            setFormData(prev => {
                const newDept = userInfo.department_name || prev.applicantDept;
                // console.log('准备更新部门信息为:', newDept);

                return {
                    ...prev,
                    applicantName: userInfo.name || prev.applicantName,
                    applicantPhone: (userInfo.mobile || '').replace(/^\+86/, '') || prev.applicantPhone,
                    employeeId: userInfo.user_id || prev.employeeId,
                    applicantOpenId: userInfo.open_id || userInfo.user_id || prev.applicantOpenId,
                    applicantDept: newDept,
                };
            });
        }
    }, [userInfo]);
    
    // 监听申请时间和车牌号的变化，重新获取车辆已预约时间
    useEffect(() => {
        if (formData.plateNumber) {
            fetchVehicleUsageTimes(formData.plateNumber)
        }
    }, [formData.applyTime, formData.plateNumber, fetchVehicleUsageTimes])
    
    // 监听formData变化，保存到localStorage
    useEffect(() => {
        localStorage.setItem('reserveFormData', JSON.stringify(formData));
    }, [formData]);
    
    // 从sessionStorage获取选择的车辆信息，每次页面加载或路径变化时执行
    useEffect(() => {
        // 延迟执行，确保sessionStorage中的数据已被正确设置
        const timer = setTimeout(() => {
            // 检查是否有选择的车辆信息
            const selectedVehicle = sessionStorage.getItem('selectedVehicle');
            if (selectedVehicle) {
                try {
                    // console.log('获取到选择的车辆信息:', selectedVehicle);
                    const vehicleData = JSON.parse(selectedVehicle);
                    // console.log('解析后的车辆信息:', vehicleData);
                    
                    // 更新车牌号和预约时间
                    const destinationStr = vehicleData.toLocation || '';
                    // 同步更新目的地数组
                    if (destinationStr) {
                        setDestinations(destinationStr.split('，').filter(d => d.trim()));
                    }
                    
                    setFormData(prev => {
                        // 处理预约时间格式，确保datetime-local输入框能正确显示
                        // 从sessionStorage获取的时间格式是 "YYYY-MM-DD HH:mm:ss"
                        // datetime-local期望的格式是 "YYYY-MM-DDTHH:mm"
                        let processedStartTime = prev.expectedStartTime;
                        let processedEndTime = prev.expectedEndTime;
                        
                        if (vehicleData.selectedStartTime) {
                            // 转换开始时间格式
                            // 将 "YYYY-MM-DD HH:mm:ss" 转换为 "YYYY-MM-DDTHH:mm:ss"
                            processedStartTime = vehicleData.selectedStartTime
                                .replace(' ', 'T');  // 将空格替换为T
                        }
                        
                        if (vehicleData.selectedEndTime) {
                            // 转换结束时间格式
                            processedEndTime = vehicleData.selectedEndTime
                                .replace(' ', 'T');  // 将空格替换为T
                        }
                        
                        return {
                            ...prev,
                            plateNumber: vehicleData.plateNumber,
                            expectedStartTime: processedStartTime,
                            expectedEndTime: processedEndTime,
                            expectedLocation: vehicleData.fromLocation || prev.expectedLocation,
                            destination: destinationStr || prev.destination,
                            reason: vehicleData.reason || prev.reason
                        };
                    });
                    
                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedVehicle');
                    // console.log('车辆信息赋值完成，已清除sessionStorage中的数据');
                    return;
                } catch (error) {
                    console.error('解析车辆信息失败:', error);
                    // console.error('原始车辆信息:', selectedVehicle);
                    sessionStorage.removeItem('selectedVehicle');
                }
            }
            
            // 检查是否有新的位置信息格式（包含出发地和目的地）
            const selectedLocations = sessionStorage.getItem('selectedLocations');
            if (selectedLocations) {
                try {
                    const locationsData = JSON.parse(selectedLocations);
                    // console.log('获取到选择的出发地和目的地信息:', locationsData);
                    
                    // 同时更新出发地和目的地
                    setFormData(prev => ({
                        ...prev,
                        expectedLocation: locationsData.from.name,
                        destination: locationsData.to.name
                    }));
                    
                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedLocations');
                    console.log('位置信息赋值完成，已清除sessionStorage中的数据');
                    return;
                } catch (error) {
                    console.error('解析位置信息失败:', error);
                    sessionStorage.removeItem('selectedLocations');
                }
            }
            
            // 兼容旧的位置信息格式
            const selectedLocation = sessionStorage.getItem('selectedLocation');
            if (selectedLocation) {
                try {
                    const locationData = JSON.parse(selectedLocation);
                    // console.log('获取到选择的位置信息:', locationData);
                    
                    // 根据type赋值给对应的输入框，只更新对应的字段
                    if (locationData.type === 'from') {
                        // 赋值给出发地，保持目的地不变
                        setFormData(prev => ({
                            ...prev,
                            expectedLocation: locationData.location.name
                        }));
                    } else if (locationData.type === 'to') {
                        // 赋值给目的地，保持出发地不变
                        updateDestinationFromMap(locationData.location.name);
                    }
                    
                    // 清除sessionStorage中的数据，避免重复赋值
                    sessionStorage.removeItem('selectedLocation');
                    console.log('位置信息赋值完成，已清除sessionStorage中的数据');
                } catch (error) {
                    console.error('解析位置信息失败:', error);
                    sessionStorage.removeItem('selectedLocation');
                }
            }
            
            // 检查是否有单独存储的出发地和目的地信息
            const fromLocation = sessionStorage.getItem('fromLocation');
            const toLocation = sessionStorage.getItem('toLocation');
            if (fromLocation || toLocation) {
                // console.log('获取到单独存储的位置信息:', { fromLocation, toLocation });
                
                // 更新表单数据
                setFormData(prev => {
                    const newDestination = toLocation || prev.destination;
                    // 同步更新目的地数组
                    if (newDestination) {
                        setDestinations(newDestination.split('，').filter(d => d.trim()));
                    }
                    return {
                        ...prev,
                        expectedLocation: fromLocation || prev.expectedLocation,
                        destination: newDestination
                    };
                });
                
                // 清除sessionStorage中的数据，避免重复赋值
                sessionStorage.removeItem('fromLocation');
                sessionStorage.removeItem('toLocation');
                console.log('单独存储的位置信息赋值完成，已清除sessionStorage中的数据');
            }
        }, 100);
        
        return () => clearTimeout(timer);
    }, [location])
    
    // 监听location变化，当从非地图页面进入预约页面时，初始化表单数据
    useEffect(() => {
        // 检查当前路径是否为预约页面
        if (location.pathname === '/reserve') {
            // 获取前一个路径（可以通过document.referrer获取，但有局限性）
            // 这里我们采用一种更简单的策略：
            // 如果是直接进入预约页面（不是从地图页面返回），则检查是否需要重新初始化表单
            
            // 从sessionStorage获取来源信息
            const fromMapPage = sessionStorage.getItem('fromMapPage');
            
            if (!fromMapPage) {
                // 不是从地图页面返回，检查是否需要清除旧数据
                // 这里我们可以添加更复杂的逻辑，比如检查是否是首次进入或刷新页面
                // 简单起见，我们不在这里做特殊处理，保持现有的初始化逻辑
            }
            
            // 清除来源标记
            sessionStorage.removeItem('fromMapPage');
        } else if (location.pathname.startsWith('/map-select/')) {
            // 前往地图页面，设置来源标记
            sessionStorage.setItem('fromMapPage', 'true');
        } else {
            // 前往其他页面，清除表单数据
            localStorage.removeItem('reserveFormData');
        }
    }, [location.pathname])

    const handleChange = (e) => {
        const { name, value } = e.target
        // 确保时间字段包含秒数
        let processedValue = value
        if (name === 'expectedStartTime' || name === 'expectedEndTime') {
            if (value.length === 16) {
                // 将value格式化为YYYY-MM-DDTHH:mm:ss
                processedValue = value + ':00'
            }
        }
        
        // 更新表单数据
        setFormData(prev => {
            const newFormData = {
                ...prev,
                [name]: processedValue
            }
            
            // 当修改开始时间或结束时间时，检查时间冲突
            if ((name === 'expectedStartTime' || name === 'expectedEndTime') && newFormData.plateNumber) {
                // 延迟检查，确保表单数据已更新
                setTimeout(() => {
                    // 构建临时的时间冲突检查函数，使用新的表单数据
                    const checkTimeConflictOnChange = () => {
                        const startTime = new Date(newFormData.expectedStartTime)
                        const endTime = new Date(newFormData.expectedEndTime)
                        
                        // 检查开始时间是否晚于结束时间
                        if (startTime >= endTime) {
                            showToast('开始时间不能晚于或等于结束时间', 'error')
                            return true
                        }
                        
                        // 检查是否与已预约时间冲突
                        for (const usage of vehicleUsageTimes) {
                            const usageStart = new Date(usage.startTime)
                            const usageEnd = new Date(usage.endTime)
                            
                            // 时间冲突的情况：
                            // 1. 新预约的开始时间在已预约时间范围内
                            // 2. 新预约的结束时间在已预约时间范围内
                            // 3. 新预约的时间范围完全包含已预约时间范围
                            if (
                                (startTime >= usageStart && startTime < usageEnd) ||
                                (endTime > usageStart && endTime <= usageEnd) ||
                                (startTime <= usageStart && endTime >= usageEnd)
                            ) {
                                showToast('预约时间与车辆已预约时间冲突，请重新选择时间', 'error')
                                return true
                            }
                        }
                        
                        return false
                    }
                    
                    checkTimeConflictOnChange()
                }, 100)
            }
            
            return newFormData
        })
    }


    // 检查时间是否与已预约时间冲突
    const checkTimeConflict = () => {
        const startTime = new Date(formData.expectedStartTime)
        const endTime = new Date(formData.expectedEndTime)
        
        // 检查开始时间是否晚于结束时间
        if (startTime >= endTime) {
            showToast('开始时间不能晚于或等于结束时间', 'error')
            return true
        }
        
        // 检查是否与已预约时间冲突
        for (const usage of vehicleUsageTimes) {
            const usageStart = new Date(usage.startTime)
            const usageEnd = new Date(usage.endTime)
            
            // 时间冲突的情况：
            // 1. 新预约的开始时间在已预约时间范围内
            // 2. 新预约的结束时间在已预约时间范围内
            // 3. 新预约的时间范围完全包含已预约时间范围
            if (
                (startTime >= usageStart && startTime < usageEnd) ||
                (endTime > usageStart && endTime <= usageEnd) ||
                (startTime <= usageStart && endTime >= usageEnd)
            ) {
                showToast('预约时间与车辆已预约时间冲突，请重新选择时间', 'error')
                return true
            }
        }
        
        return false
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        // 防止重复提交
        if (submitting) {
            return
        }
        
        // 检查时间冲突
        if (checkTimeConflict()) {
            return
        }
        
        try {
            setSubmitting(true)
            // 构建请求参数
            const requestData = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                plateNumber: formData.plateNumber,
                applicantId: formData.employeeId,
                applicantName: formData.applicantName,
                applicantPhone: formData.applicantPhone,
                applicantDept: formData.applicantDept,
                applyTime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                reason: formData.reason,
                expectedStartTime: formData.expectedStartTime.replace('T', ' '),
                expectedEndTime: formData.expectedEndTime.replace('T', ' '),
                expectedLocation: formData.expectedLocation,
                destination: formData.destination,
                driveType: parseInt(formData.driveType),
                isPersonal: isPersonal,
                vehicleUserName: formData.vehicleUserName, // 用车人姓名
                vehicleUserCode: formData.vehicleUserCode, // 用车人工号
                 passengers: formData.passengers,
                 passengersCode: formData.passengersCode
            }

            console.log('提交预约信息:', requestData)

            // 调用后端API
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/submit`, requestData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            console.log('API响应:', response.data)

            // 处理响应
            if (response.data && response.data.code === 200) {
                showToast('预约成功！', 'success')
                // 清除localStorage中的表单数据
                localStorage.removeItem('reserveFormData');
                sessionStorage.removeItem('reservePageInitialized');
                navigate('/')
            } else {
                showToast(`预约失败: ${response.data.message || '未知错误'}`, 'error')
            }
        } catch (error) {
            console.error('提交预约失败:', error)
            showToast('网络错误，请稍后重试', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCancel = () => {
        // 清除localStorage中的表单数据
        localStorage.removeItem('reserveFormData');
        navigate('/')
    }

    // 跳转到地图选择页面选择出发地
    const handleChooseFromLocation = () => {
        // 存储当前页面路径到sessionStorage
        sessionStorage.setItem('previousPath', '/reserve');
        console.log('存储当前页面路径到sessionStorage:', '/reserve');
        navigate('/map-select/from')
    }

    // 跳转到地图选择页面选择目的地
    const handleChooseToLocation = () => {
        // 存储当前页面路径到sessionStorage
        sessionStorage.setItem('previousPath', '/reserve');
        console.log('存储当前页面路径到sessionStorage:', '/reserve');
        navigate('/map-select/to')
    }
    
    // 添加目的地
    const handleAddDestination = () => {
        if (destinations.length >= 5) {
            showToast('目的地最多只能添加5个', 'error');
            return;
        }
        if (newDestinationInput.trim()) {
            const newDestinations = [...destinations, newDestinationInput.trim()];
            setDestinations(newDestinations);
            setFormData(prev => ({
                ...prev,
                destination: newDestinations.join('，')
            }));
            // 清空输入框并隐藏
            setNewDestinationInput('');
            setShowAddDestination(false);
        }
    };
    
    // 删除目的地
    const handleRemoveDestination = (index) => {
        const newDestinations = destinations.filter((_, i) => i !== index);
        setDestinations(newDestinations);
        setFormData(prev => ({
            ...prev,
            destination: newDestinations.join('，')
        }));
    };
    
    // 从地图选择返回时更新目的地
    const updateDestinationFromMap = (locationName) => {
        if (locationName) {
            handleAddDestination(locationName);
        }
    };

    // 处理联系人选择结果
    const handleContactSelectResult = (res) => {
        console.log('处理联系人选择结果:', res);

        // 处理选择的用户
        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用户:', selectedUsers);

            // 过滤掉申请人自己（不能选择申请人作为同行人）和已选择的用车人
            const vehicleUserIds = formData.vehicleUserCode ? formData.vehicleUserCode.split(',').filter(id => id.trim()) : [];
            let filteredByApplicant = 0;
            let filteredByDriver = 0;
            
            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                // 排除申请人自己
                if (userOpenId === formData.applicantOpenId) {
                    filteredByApplicant++;
                    return false;
                }
                // 排除已选择的用车人
                if (vehicleUserIds.includes(userOpenId)) {
                    filteredByDriver++;
                    return false;
                }
                return true;
            });

            // 如果没有有效用户，提示错误
            if (filteredUsers.length === 0) {
                if (filteredByApplicant > 0 && filteredByDriver > 0) {
                    showToast('不能选择申请人或已选择的用车人作为同行人，请重新选择', 'error');
                } else if (filteredByApplicant > 0) {
                    showToast('不能选择申请人作为同行人，请重新选择', 'error');
                } else if (filteredByDriver > 0) {
                    showToast('不能选择已选择的用车人作为同行人，请重新选择', 'error');
                } else {
                    showToast('请重新选择同行人', 'error');
                }
                return;
            }

            // 如果过滤掉了部分用户，提示用户
            if (filteredUsers.length < selectedUsers.length) {
                let message = '已排除：';
                const messages = [];
                if (filteredByApplicant > 0) {
                    messages.push('申请人');
                }
                if (filteredByDriver > 0) {
                    messages.push('已选择的用车人');
                }
                message += messages.join('、');
                message += '，不能选择作为同行人';
                showToast(message, 'error');
            }

            // 提取姓名和openId,分别用逗号分隔
            const names = filteredUsers.map(user => user.name).filter(name => name);
            const openIds = filteredUsers.map(user => user.openId).filter(openId => openId);

            console.log('提取的姓名:', names);
            console.log('提取的openId:', openIds);

            // 更新表单数据
            setFormData(prev => ({
                ...prev,
                passengers: names.join(','), // 逗号分隔的姓名
                passengersCode: openIds.join(',') // 逗号分隔的openId
            }));

            showToast(`已选择 ${names.length} 位联系人`, 'success');
        } else {
            console.warn('未选择联系人');
            showToast('未选择联系人', 'error');
        }
    };

    // 打开飞书联系人选择器
    const openLarkContactPicker = async () => {
        console.log('openLarkContactPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 检测是否在开发者工具中
        const isDevTools = window.navigator.userAgent.toLowerCase().includes('devtools') ||
                          window.location.href.includes('devtools');
        if (isDevTools) {
            console.log('检测到开发者工具环境，使用模拟数据');
            // 在开发者工具中使用模拟数据
            const mockData = {
                data: [
                    { openId: 'ou_dev_test_1', name: '测试同行人1', mobile: '+8613800138001' },
                    { openId: 'ou_dev_test_2', name: '测试同行人2', mobile: '+8613800138002' }
                ],
                department_data: []
            };
            handleContactSelectResult(mockData);
            return;
        }

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开联系人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact');
        tt.chooseContact({
            multi: true,
            ignore: false,
            maxNum: 10,
            limitTips: "最多只能选择10个同行人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleContactSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择同行人:', res);
            }
        });
    };

    // 打开用车人选择器
    const openDriverPicker = async () => {
        console.log('openDriverPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        // 检测是否在开发者工具中
        const isDevTools = window.navigator.userAgent.toLowerCase().includes('devtools') ||
                          window.location.href.includes('devtools');
        if (isDevTools) {
            console.log('检测到开发者工具环境，使用模拟数据');
            const mockData = {
                data: [
                    { openId: 'ou_dev_test_1', name: '测试司机1', mobile: '+8613800138001' }
                ],
                department_data: []
            };
            handleDriverSelectResult(mockData);
            return;
        }

        // 确保JSAPI鉴权完成
        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                // 重新进行JSAPI鉴权
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            showToast('飞书JSAPI鉴权失败，无法打开用车人选择', 'error');
            return;
        }

        const tt = window.h5sdk.tt || window.tt;
        console.log('tt对象:', tt);
        console.log('tt.chooseContact:', tt?.chooseContact);

        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持用车人选择', 'error');
            return;
        }

        console.log('开始调用 tt.chooseContact 选择用车人');
        tt.chooseContact({
            multi: false, // 用车人只能选一个
            ignore: false,
            maxNum: 1,
            limitTips: "只能选择1个用车人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleDriverSelectResult(res);
            },
            fail: (res) => {
                // 用户取消选择或关闭选择器，不报错
                console.log('取消选择用车人:', res);
            }
        });
    };

    // 处理用车人选择结果
    const handleDriverSelectResult = (res) => {
        console.log('处理用车人选择结果:', res);

        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用车人:', selectedUsers);

            // 过滤掉申请人自己（不能选择申请人作为用车人）和已选择的同行人
            const passengerIds = formData.passengersCode ? formData.passengersCode.split(',').filter(id => id.trim()) : [];
            let filteredByApplicant = 0;
            let filteredByPassenger = 0;
            
            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                // 排除申请人自己
                if (userOpenId === formData.applicantOpenId) {
                    filteredByApplicant++;
                    return false;
                }
                // 排除已选择的同行人
                if (passengerIds.includes(userOpenId)) {
                    filteredByPassenger++;
                    return false;
                }
                return true;
            });

            // 如果没有有效用户，提示错误
            if (filteredUsers.length === 0) {
                if (filteredByApplicant > 0 && filteredByPassenger > 0) {
                    showToast('不能选择申请人或已选择的同行人作为用车人，请重新选择', 'error');
                } else if (filteredByApplicant > 0) {
                    showToast('不能选择申请人作为用车人，请重新选择', 'error');
                } else if (filteredByPassenger > 0) {
                    showToast('不能选择已选择的同行人作为用车人，请重新选择', 'error');
                } else {
                    showToast('请重新选择用车人', 'error');
                }
                return;
            }

            // 如果过滤掉了部分用户，提示用户
            if (filteredUsers.length < selectedUsers.length) {
                let message = '已排除：';
                const messages = [];
                if (filteredByApplicant > 0) {
                    messages.push('申请人');
                }
                if (filteredByPassenger > 0) {
                    messages.push('已选择的同行人');
                }
                message += messages.join('、');
                message += '，不能选择作为用车人';
                showToast(message, 'error');
            }

            // 提取姓名和user_id,分别用逗号分隔
            // 兼容不同的字段名: open_id, openId, user_id
            const names = filteredUsers.map(user => user.name).filter(name => name);
            const userIds = filteredUsers.map(user => {
                // 优先获取 user_id,其次是 open_id,最后是 openId
                return user.user_id || user.open_id || user.openId;
            }).filter(userId => userId);

            console.log('提取的姓名:', names);
            console.log('提取的user_id:', userIds);

            // 更新表单数据
            setFormData(prev => ({
                ...prev,
                vehicleUserName: names.join(','), // 逗号分隔的姓名
                vehicleUserCode: userIds.join(',') // 逗号分隔的user_id
            }));

            showToast(`已选择 ${names.length} 个用车人`, 'success');
        } else {
            console.warn('未选择用车人');
            showToast('未选择用车人', 'error');
        }
    };

return (
    <div className="reserve-page">
        <h2>车辆预约</h2>
        {userLoading && (
            <div className="loading-indicator">
                <p>正在获取用户信息...</p>
            </div>
        )}
        <form onSubmit={handleSubmit} className="reserve-form">
            <div className="form-group">
                <label>申请人:<span className="required">*</span></label>
                <input
                    type="text"
                    name="applicantName"
                    value={formData.applicantName}
                    onChange={handleChange}
                    required
                    readOnly
                />
            </div>
            <div className="form-group">
                <label>电话号码:<span className="required">*</span></label>
                <input
                    type="tel"
                    name="applicantPhone"
                    value={formData.applicantPhone}
                    onChange={handleChange}
                    required
                    readOnly
                />
            </div>
            <div className="form-group" style={{ display: 'none' }}>
                <label>工号:<span className="required">*</span></label>
                <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange}
                    required
                    readOnly
                />
            </div>
            <div className="form-group">
                <label>所属部门:<span className="required">*</span></label>
                <input
                    type="text"
                    name="applicantDept"
                    value={formData.applicantDept}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="form-group" style={{ flexDirection: 'row', gap: '20px', alignItems: 'center', flexWrap: 'nowrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 'normal', fontSize: '16px', minWidth: 'auto', margin: 0 }}>
                    <input
                        type="radio"
                        name="applyType"
                        checked={isPersonal === 0}
                        onChange={() => {
                            setIsPersonal(0);
                            setFormData(prev => ({ ...prev, vehicleUserName: '', vehicleUserCode: '' }));
                        }}
                        style={{ margin: 0, width: 'auto', flex: 'none' }}
                    />
                    本人申请
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 'normal', fontSize: '16px', minWidth: 'auto', margin: 0 }}>
                    <input
                        type="radio"
                        name="applyType"
                        checked={isPersonal === 1}
                        onChange={() => setIsPersonal(1)}
                        style={{ margin: 0, width: 'auto', flex: 'none' }}
                    />
                    代人申请
                </label>
            </div>
            {isPersonal === 1 && (
                <div className="form-group">
                    <label>用车人:<span className="required">*</span></label>
                    <div className="input-with-button">
                        <input
                            type="text"
                            name="vehicleUserName"
                            value={formData.vehicleUserName}
                            onChange={handleChange}
                            placeholder="选择用车人(最多1个)"
                            // required
                            readOnly
                            onClick={openDriverPicker}
                        />
                        <button
                            type="button"
                            onClick={openDriverPicker}
                            className="input-button"
                            title="选择用车人"
                        >
                            👤
                        </button>
                    </div>
                </div>
            )}
            <div className="form-group">
                <label>申请日期:<span className="required">*</span></label>
                <input
                    type="date"
                    name="applyTime"
                    value={formData.applyTime}
                    onChange={handleChange}
                    required
                    readOnly
                />
            </div>
            <div className="form-group">
                <label>车牌号:<span className="required">*</span></label>
                <div className="input-with-button">
                    <input
                        type="text"
                        name="plateNumber"
                        value={formData.plateNumber}
                        onChange={handleChange}
                        required
                        readOnly
                        placeholder="请选择车牌号"
                    />
                    <button
                        type="button"
                        onClick={() => navigate('/vehicle-select')}
                        className="input-button"
                        title="选择车辆"
                    >
                        🚗
                    </button>
                </div>
            </div>
            <div className="form-group">
                <label>开始时间:<span className="required">*</span></label>
                <input
                        type="datetime-local"
                        name="expectedStartTime"
                        value={formData.expectedStartTime.substring(0, 16)}
                        onChange={handleChange}
                        required
                        step="60"
                    />
            </div>
            <div className="form-group">
                <label>结束时间:<span className="required">*</span></label>
                <input
                        type="datetime-local"
                        name="expectedEndTime"
                        value={formData.expectedEndTime.substring(0, 16)}
                        onChange={handleChange}
                        required
                        step="60"
                    />
            </div>
            <div className="form-group">
                <label>出发地:<span className="required">*</span></label>
                <div className="input-with-button">
                    <input
                        type="text"
                        name="expectedLocation"
                        value={formData.expectedLocation}
                        onChange={handleChange}
                        required
                    />
                    <button
                        type="button"
                        onClick={handleChooseFromLocation}
                        className="input-button"
                    >
                        📍
                    </button>
                </div>
            </div>
            <div className="form-group location-group">
                <label>目的地:<span className="required">*</span></label>
                <div className="input-with-button">
                    <input
                        type="text"
                        name="destination"
                        value={formData.destination}
                        onChange={(e) => {
                            handleChange(e);
                            // 同步更新目的地数组
                            const value = e.target.value;
                            if (value) {
                                setDestinations(value.split('，').filter(d => d.trim()));
                            } else {
                                setDestinations([]);
                            }
                        }}
                        required
                        title={formData.destination}
                    />
                    <button
                        type="button"
                        onClick={handleChooseToLocation}
                        className="input-button"
                    >
                        📍
                    </button>
                </div>
                {/* 已添加的目的地标签 */}
                {destinations.length > 0 && (
                    <div className="destinations-tags">
                        {destinations.map((dest, index) => (
                            <span key={index} className="destination-tag">
                                {dest}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveDestination(index)}
                                    className="remove-tag-btn"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                {/* 添加目的地按钮 */}
                <button
                    type="button"
                    onClick={() => {
                        if (destinations.length >= 5) {
                            showToast('目的地最多只能添加5个', 'error');
                            return;
                        }
                        setShowAddDestination(!showAddDestination);
                    }}
                    className="add-destination-btn"
                    disabled={destinations.length >= 5}
                >
                    ➕ 添加目的地 ({destinations.length}/5)
                </button>
                {/* 添加目的地输入框 */}
                {showAddDestination && (
                    <div className="additional-destination-container">
                        <input
                            type="text"
                            value={newDestinationInput}
                            onChange={(e) => setNewDestinationInput(e.target.value)}
                            className="location-input additional-destination-input"
                            placeholder="请输入目的地"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddDestination();
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={handleAddDestination}
                            className="confirm-add-btn"
                        >
                            确认添加
                        </button>
                    </div>
                )}
            </div>

            <div className="form-group">
                <label>同行人:</label>
                <div className="input-with-button">
                    <input
                        type="text"
                        name="passengers"
                        value={formData.passengers}
                        onChange={handleChange}
                        placeholder="选择同行人(最多10个)"
                        readOnly
                        onClick={openLarkContactPicker}
                    />
                    <button
                        type="button"
                        onClick={openLarkContactPicker}
                        className="input-button"
                        title="选择同行人"
                    >
                        👥
                    </button>
                </div>
            </div>

            <div className="form-group">
                <label>用车原因:<span className="required">*</span></label>
                <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows={4}
                    required
                ></textarea>
            </div>
            <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? '提交中...' : '立即预约'}
                </button>
                <button type="button" className="cancel-btn" onClick={handleCancel}>取消</button>
            </div>
        </form>
    </div>
);
}